import React, { createRef } from "react";
import { NSBox, NSBoxBoolean, NSBoxBooleans, NSBoxEntity, NSBoxInteger, NSBoxString, NSBoxTextArea, NSColumn, NSLabel, NSRow } from "namirasoft-site-react";
import { NSARouterMakerResult } from "namirasoft-account-react";
import { BillWatchEntities } from "../BillWatchEntities";
import { BillWatchProps } from "../BillWatchProps";
import { BillWatchLayout } from '../BillWatchLayout';
import { AWSConstants, AWSFullRow, AWSInputRow, AWSRow } from "namirasoft-bill-watch";
import { AWSRow as CredentialAWSRow } from "namirasoft-credential";
import { TopicRow } from "namirasoft-notification-sender";
import { LogGroupRow } from "namirasoft-log";
import { NamirasoftMap } from "namirasoft-site-map";
import Rules from "../components/Rules";
import LookbackPeriods from "../components/LookbackPeriods";
import { NamingConvention, StringOperation } from "namirasoft-core";
import { PaymentRequired } from "namirasoft-account-react";
import { NSCCostChart } from "namirasoft-cost-react";

interface AWSEditPageProps extends NSARouterMakerResult, BillWatchProps
{
    isEdit: boolean;
}

export default class AWSEditPage extends React.Component<AWSEditPageProps>
{
    NSBoxEntity_CredentialID = createRef<NSBoxEntity<CredentialAWSRow>>();
    NSBoxString_AccountID = createRef<NSBoxString>();
    NSBoxEntity_TopicID = createRef<NSBoxEntity<TopicRow>>();
    NSBoxEntity_LogGroupID = createRef<NSBoxEntity<LogGroupRow>>();
    NSBoxString_Name = createRef<NSBoxString>();
    NSBoxBoolean_Active = createRef<NSBoxBoolean>();
    NSBoxBoolean_ShowAPICall = createRef<NSBoxBoolean>();
    NSBoxBoolean_ShowNewCost = createRef<NSBoxBoolean>();
    NSBoxBoolean_ShowOnDeactive = createRef<NSBoxBoolean>();
    NSBoxBoolean_ShowOnNoAnomaly = createRef<NSBoxBoolean>();
    NSBoxInteger_MaxAPICallTotal = createRef<NSBoxInteger>();
    NSBoxInteger_MaxAPICallPerOperation = createRef<NSBoxInteger>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();
    Rules_Main = createRef<Rules>();
    LookbackPeriods_Main = createRef<LookbackPeriods>();
    NSBoxBooleans_Dimensions = createRef<NSBoxBooleans>();
    NSBoxBooleans_Metrics = createRef<NSBoxBooleans>();

    override render()
    {
        let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);
        return (
            <PaymentRequired
                {...this.props}
                tilte="Subscription is Required to Start Watching"
                description="Want to start watching your AWS usage? Subscribe to activate Bill Watch and begin tracking your costs with confidence. New users enjoy a 2-week free trial with no upfront fees. Subscribers may cancel their subscription at any time with no extra cost."
                button={{ action: "Activate" }}
                payment_offer_url="https://payment.namirasoft.com/offer/list/cost/usage"
                Validation={this.props.servers.bill_watch.aws.Validation}
            >
                <BillWatchLayout<AWSRow, AWSInputRow>
                    key="main"
                    {...this.props}
                    action={{
                        description: this.props.isEdit ? "You can edit a previous AWS watch and change its settings." : "You can create a new watch for AWS to monitor costs. Enter the required information, including account credentials and watch parameters.",
                    }}
                    style={{ padding: 0 }}
                    pages={{
                        entity: BillWatchEntities.aws(this.props, () => { }),
                        edit: {
                            ...this.props,
                            ui: {
                                tab_page_edit: {
                                    editor: {
                                        getInputRow: () =>
                                        {
                                            let credential_id: string = this.NSBoxEntity_CredentialID.current?.getValueOne() ?? "";
                                            let account_id: string = this.NSBoxString_AccountID.current?.getValue() ?? "";
                                            let topic_id: string = this.NSBoxEntity_TopicID.current?.getValueOne() ?? "";
                                            let log_group_id: string = this.NSBoxEntity_LogGroupID.current?.getValueOne() ?? "";
                                            let name: string = this.NSBoxString_Name.current?.getValue() ?? "";
                                            let active: boolean = this.NSBoxBoolean_Active.current?.getValue() ?? false;
                                            let show_api_call: boolean = this.NSBoxBoolean_ShowAPICall.current?.getValue() ?? false;
                                            let show_new_cost: boolean = this.NSBoxBoolean_ShowNewCost.current?.getValue() ?? false;
                                            let show_on_deactive: boolean = this.NSBoxBoolean_ShowOnDeactive.current?.getValue() ?? false;
                                            let show_on_no_anomaly: boolean = this.NSBoxBoolean_ShowOnNoAnomaly.current?.getValue() ?? false;
                                            let max_api_call_total: number = this.NSBoxInteger_MaxAPICallTotal.current?.getValue() ?? 0;
                                            let max_api_call_per_operation: number = this.NSBoxInteger_MaxAPICallPerOperation.current?.getValue() ?? 0;
                                            let rule_ids = this.Rules_Main.current?.NSRepeater_Rules.current?.getValue() ?? [];
                                            let lookback_periods = this.LookbackPeriods_Main.current?.NSRepeater_LookbackPeriods.current?.getValue() ?? [];
                                            let dimensions = this.NSBoxBooleans_Dimensions.current?.getValues() ?? [];
                                            let metrics = this.NSBoxBooleans_Metrics.current?.getValues() ?? [];
                                            let description: string = this.NSBoxTextArea_Description.current?.getValue() ?? "";
                                            return {
                                                workspace_id: "",
                                                credential_id,
                                                account_id,
                                                topic_id,
                                                log_group_id,
                                                name,
                                                active,
                                                show_api_call,
                                                show_new_cost,
                                                show_on_deactive,
                                                show_on_no_anomaly,
                                                max_api_call_total,
                                                max_api_call_per_operation,
                                                rule_ids,
                                                lookback_periods,
                                                dimensions,
                                                metrics,
                                                description,
                                                aws_category: [],
                                                aws_field: [],
                                                aws_tag: [],
                                            };
                                        },
                                        setRow: (row) =>
                                        {
                                            let full_row = row as AWSFullRow;
                                            this.NSBoxEntity_CredentialID.current?.setValue(row.credential_id);
                                            this.NSBoxString_AccountID.current?.setValue(row.account_id);
                                            this.NSBoxEntity_TopicID.current?.setValue(row.topic_id);
                                            this.NSBoxEntity_LogGroupID.current?.setValue(row.log_group_id);
                                            this.NSBoxString_Name.current?.setValue(row.name);
                                            this.NSBoxBoolean_Active.current?.setValue(row.active);
                                            this.NSBoxBoolean_ShowAPICall.current?.setValue(row.show_api_call);
                                            this.NSBoxBoolean_ShowNewCost.current?.setValue(row.show_new_cost);
                                            this.NSBoxBoolean_ShowOnDeactive.current?.setValue(row.show_on_deactive);
                                            this.NSBoxBoolean_ShowOnNoAnomaly.current?.setValue(row.show_on_no_anomaly);
                                            this.NSBoxInteger_MaxAPICallTotal.current?.setValue(row.max_api_call_total);
                                            this.NSBoxInteger_MaxAPICallPerOperation.current?.setValue(row.max_api_call_per_operation);
                                            this.Rules_Main.current?.NSRepeater_Rules.current?.setValue(full_row.aws_rule.map(r => r.rule_id));
                                            this.LookbackPeriods_Main.current?.NSRepeater_LookbackPeriods.current?.setValue(full_row.lookback_periods);
                                            this.NSBoxBooleans_Dimensions.current?.setValues(StringOperation.split(row.dimensions));
                                            this.NSBoxBooleans_Metrics.current?.setValues(StringOperation.split(row.metrics));
                                            this.NSBoxTextArea_Description.current?.setValue(row.description);
                                        },
                                    },
                                    ui: {
                                        extra_tabs: [
                                            {
                                                title: "Rules",
                                                clicked: true,
                                                info: {
                                                    link: "https://namirasoft.com/bill-watch/console-guide/aws/#field-rules"
                                                },
                                                getContent: () =>
                                                {
                                                    return <Rules
                                                        ref={this.Rules_Main}
                                                        {...this.props}
                                                        name="AWSEditPage"
                                                        info={{ link: "https://namirasoft.com/bill-watch/console-guide/aws/#field-rules" }}
                                                    />;
                                                }
                                            },
                                            {
                                                title: "Lookback Periods",
                                                clicked: true,
                                                info: {
                                                    link: "https://namirasoft.com/bill-watch/console-guide/aws/#field-lookback-periods"
                                                },
                                                getContent: () =>
                                                {
                                                    return <LookbackPeriods
                                                        ref={this.LookbackPeriods_Main}
                                                        {...this.props}
                                                        name="AWSEditPage"
                                                        info={{ link: "https://namirasoft.com/bill-watch/console-guide/aws/#field-lookback-periods" }}
                                                    />;
                                                }
                                            },
                                            {
                                                title: "Dimensions",
                                                clicked: true,
                                                info: {
                                                    link: "https://namirasoft.com/bill-watch/console-guide/aws/#field-dimensions"
                                                },
                                                getContent: () =>
                                                {
                                                    return <NSRow style={{ maxWidth: NSBox.width.triple }}>
                                                        <NSLabel
                                                            title="❗️Daily Dimension Checks & Cost Notice!"
                                                            description="🔲 Each selected dimension is automatically checked once a day using AWS API calls.<br/>
                                                                For every check, our service sends a request to AWS, and each API call costs approximately $0.01 per page (estimated; actual cost may vary).<br/>
                                                                💬 There are two types of checks listed below:<br/>
                                                                1️⃣ By Total: Which is on the entire account and only needs one API call. (✅ Recomeneded)<br/>
                                                                2️⃣ By Single Dimensions: Checked individually with one API call per dimension. (✅ Recomeneded)<br/>
                                                                3️⃣ By Dimension Pairs: Combine two dimensions and usually require scanning more pages, resulting in higher API usage and cost. (⚠️ Select them based on your requirement)<br/>
                                                                💰 The total daily cost depends on how many dimensions (or pairs) you select and how much data is involved.<br/>
                                                                Use the checkboxes below to enable or disable specific checks and better control your usage and costs."
                                                            classList={["nsbw-label"]}
                                                        >
                                                            <a href='https://namirasoft.com/bill-watch/console-guide/aws/#field-dimensions' target='_blank' rel="noreferrer" style={{ marginLeft: "4px" }}>Read More</a>
                                                        </NSLabel>
                                                        <NSBoxBooleans
                                                            ref={this.NSBoxBooleans_Dimensions}
                                                            items={AWSConstants.getDimensionCombinations().map(r =>
                                                            {
                                                                let keys = r.keys;
                                                                if (keys.length === 0)
                                                                    keys = ["Total"];
                                                                return {
                                                                    value: r.name,
                                                                    text: NamingConvention.auto.convert("By " + keys.join(" and "), NamingConvention.Pascal_Case_Space)
                                                                };
                                                            })}
                                                            box_all={{ hideHeader: true }}
                                                            boxes={{
                                                                hideHeader: true,
                                                                defaultValue: ["TOTAL", ...AWSConstants.DIMENSIONS]
                                                            }}
                                                        />
                                                    </NSRow>;
                                                }
                                            },
                                            {
                                                title: "Metrics",
                                                clicked: true,
                                                info: {
                                                    link: "https://namirasoft.com/bill-watch/console-guide/aws/#field-metrics"
                                                },
                                                getContent: () =>
                                                {
                                                    return <NSRow style={{ maxWidth: NSBox.width.triple }}>
                                                        <NSBoxBooleans
                                                            ref={this.NSBoxBooleans_Metrics}
                                                            items={AWSConstants.METRICS.map(r =>
                                                            {
                                                                return {
                                                                    value: r,
                                                                    text: NamingConvention.auto.convert(r, NamingConvention.Pascal_Case_Space)
                                                                };
                                                            })}
                                                            box_all={{ hideHeader: true }}
                                                            boxes={{
                                                                hideHeader: true,
                                                                defaultValue: ["UnblendedCost"]
                                                            }}
                                                        />
                                                    </NSRow>;
                                                }
                                            }
                                        ]
                                    }
                                },
                                tab_page_view: {
                                    tabs: {
                                        extra: {
                                            after_history: [
                                                NSCCostChart.getTab(this.props)
                                            ]
                                        }
                                    }
                                },
                            }
                        }
                    }}
                >
                    <NSColumn>
                        <NSRow>
                            <NSBoxString
                                ref={this.NSBoxString_Name}
                                required
                                title="Name"
                                info={{ link: "https://namirasoft.com/bill-watch/console-guide/aws/#field-name" }}
                            />
                            <NSBoxBoolean
                                ref={this.NSBoxBoolean_Active}
                                required
                                defaultValue={true}
                                title="Active"
                                info={{ link: "https://namirasoft.com/bill-watch/console-guide/aws/#field-active" }}
                            />
                        </NSRow>
                        <NSRow>
                            <NSBoxEntity<CredentialAWSRow>
                                ref={this.NSBoxEntity_CredentialID}
                                required
                                title="AWS Credential"
                                multiple={false}
                                table={namirasoft.credential.tables.aws}
                                getTitle={item => item.name}
                                getValue={item => item.id}
                                getSort={() => null}
                                style={{ width: NSBox.width.double }}
                                info={{ link: "https://namirasoft.com/bill-watch/console-guide/aws/#field-credential-id" }}
                            />
                        </NSRow>
                        <NSRow>
                            <NSBoxString
                                ref={this.NSBoxString_AccountID}
                                required
                                title="AWS Account ID"
                                style={{ width: NSBox.width.double }}
                                info={{ link: "https://namirasoft.com/bill-watch/console-guide/aws/#field-account-id" }}
                            />
                        </NSRow>
                        <NSRow>
                            <NSBoxEntity<TopicRow>
                                ref={this.NSBoxEntity_TopicID}
                                required
                                title="Topic"
                                multiple={false}
                                table={namirasoft.notification_sender.tables.topic}
                                getTitle={item => item.name}
                                getValue={item => item.id}
                                getSort={() => null}
                                info={{ link: "https://namirasoft.com/bill-watch/console-guide/aws/#field-topic-id" }}
                            />
                            <NSBoxEntity<LogGroupRow>
                                ref={this.NSBoxEntity_LogGroupID}
                                required={true}
                                title="Log Group"
                                multiple={false}
                                table={namirasoft.log.tables.log_group}
                                getTitle={item => item.name}
                                getValue={item => item.id}
                                getSort={() => null}
                                info={{ link: "https://namirasoft.com/bill-watch/console-guide/aws/#field-log-group-id" }}
                            />
                        </NSRow>
                        <NSRow>
                            <NSBoxBoolean
                                ref={this.NSBoxBoolean_ShowAPICall}
                                required
                                defaultValue={true}
                                title="Show API Call"
                                info={{ link: "https://namirasoft.com/bill-watch/console-guide/aws/#field-show-api-call" }}
                            />
                            <NSBoxBoolean
                                ref={this.NSBoxBoolean_ShowNewCost}
                                required
                                defaultValue={true}
                                title="Show New Cost"
                                info={{ link: "https://namirasoft.com/bill-watch/console-guide/aws/#field-show-new-cost" }}
                            />
                        </NSRow>
                        <NSRow>
                            <NSBoxBoolean
                                ref={this.NSBoxBoolean_ShowOnDeactive}
                                required
                                defaultValue={true}
                                title="Show On Deactive"
                                info={{ link: "https://namirasoft.com/bill-watch/console-guide/aws/#field-show-on-deactive" }}
                            />
                            <NSBoxBoolean
                                ref={this.NSBoxBoolean_ShowOnNoAnomaly}
                                required
                                defaultValue={true}
                                title="Show On No Anomaly"
                                info={{ link: "https://namirasoft.com/bill-watch/console-guide/aws/#field-show-on-no-anomaly" }}
                            />
                        </NSRow>
                        <NSRow>
                            <NSBoxInteger
                                ref={this.NSBoxInteger_MaxAPICallTotal}
                                required
                                defaultValue={100}
                                title="Max API Call Total"
                                info={{ link: "https://namirasoft.com/bill-watch/console-guide/aws/#field-max-api-call-total" }}
                            />
                            <NSBoxInteger
                                ref={this.NSBoxInteger_MaxAPICallPerOperation}
                                required
                                defaultValue={5}
                                title="Max API Call Per Operation"
                                info={{ link: "https://namirasoft.com/bill-watch/console-guide/aws/#field-max-api-call-per-operation" }}
                            />
                        </NSRow>
                        <NSRow>
                            <NSBoxTextArea
                                ref={this.NSBoxTextArea_Description}
                                required={false}
                                title="Description"
                                style={{ width: NSBox.width.double }}
                                info={{ link: "https://namirasoft.com/bill-watch/console-guide/aws/#field-description" }}
                            />
                        </NSRow>
                    </NSColumn>
                </BillWatchLayout>
            </PaymentRequired>
        );
    }
}